<script lang="ts">
import { computed, defineComponent, onMounted, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import { Button as ButtonComponent } from 'beeline-design-system-vue3'
import { usePhoneStore } from '@/stores/phoneStore'
import { useLangStore } from '@/stores/langStore'
import { useLogoStore } from '@/stores/logoStore'

import successIcon from '@/assets/icons/success.svg'
import noAccessCameraIcon from '@/assets/icons/no-access-to-camera.svg'
import blockedDeviceIcon from '@/assets/icons/blocked_device.svg'
import deviceRegIcon from '@/assets/icons/device-reg.svg'
import errorIcon from '@/assets/icons/error.svg'
import fatalErrorIcon from '@/assets/icons/fatal_error.svg'
import siteUnavailableIcon from '@/assets/icons/site-unavailable.svg'

export default defineComponent({
  name: 'StatusView',
  components: { ButtonComponent },
  props: {
    showStatusBiometry: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    statusBiometry: {
      type: String,
      required: true,
    },
    restartDecs: {
      type: String,
      required: true,
    },
    restartIdentification: {
      type: Boolean,
      required: true,
      default: false,
    },
    noAccessToCamera: {
      type: Boolean,
      required: true,
      default: false,
    },
    isRedirectToMyBeeline: {
      type: Boolean,
      required: true,
      default: false,
    },
    siteUnavailable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateProp'],
  setup(props, { emit }) {
    const propRefs = toRefs(props)

    const phoneStore = usePhoneStore()
    const ctn = phoneStore.getCtn()

    const logoStore = useLogoStore()

    const router = useRouter()

    if (
      props.statusBiometry === 'error' ||
      props.statusBiometry === 'success' ||
      props.statusBiometry === 'blockedDevice' ||
      props.statusBiometry === 'deviceRegistration'
    ) {
      onMounted(() => {
        logoStore.initializeStatusView()
      })
    } else {
      logoStore.isStatusViewInitialized = true
    }

    const updateProp = (propName: string, value: any) => {
      emit('updateProp', propName, value)
    }

    const restartBiometry = () => {
      logoStore.isStatusViewInitialized = true
      emit('updateProp', !props.showStatusBiometry)
    }

    const backToStart = () => {
      router.push({ path: '/' })
    }

    const goToNumberRegistrationPage = () => {
      router.push({
        path: '/number-registration',
      })
    }

    const redirectToMyBeeline = () => {
      window.open(
        'https://redirect.appmetrica.yandex.com/serve/1181615733411454160',
        '_blank'
      )
    }

    const redirectToMoreDetails = () => {
      const lang = useLangStore().getSelectedLanguage()
      const locale = lang === 'ru' ? 'ru' : 'kk'
      window.open(`https://beeline.kz/${locale}/too-much`, '_blank')
    }

    const primaryButtonClass = computed(() => ({
      'redirect-color':
        props.isRedirectToMyBeeline ||
        props.statusBiometry === 'maximumNumberExceeded',
    }))

    const showBackToStartButton = computed(() => {
      return (
        !props.restartIdentification &&
        !props.isRedirectToMyBeeline &&
        !props.noAccessToCamera
      )
    })

    const statusIcon = computed(() => {
      if (props.siteUnavailable) {
        return siteUnavailableIcon
      } else if (props.statusBiometry === 'success' && !props.noAccessToCamera) {
        return successIcon
      } else if (
        props.noAccessToCamera &&
        props.statusBiometry === 'noAccessCamera'
      ) {
        return noAccessCameraIcon
      } else if (props.statusBiometry === 'blockedDevice') {
        return blockedDeviceIcon
      } else if (props.statusBiometry === 'deviceRegistration') {
        return deviceRegIcon
      } else if (props.statusBiometry === 'fatalError') {
        return fatalErrorIcon
      } else {
        return errorIcon
      }
    })

    const statusAlt = computed(() => {
      if (props.siteUnavailable) {
        return 'site-unavailable'
      } else if (props.statusBiometry === 'success' && !props.noAccessToCamera) {
        return 'success'
      } else if (
        props.noAccessToCamera &&
        props.statusBiometry === 'noAccessCamera'
      ) {
        return 'no-access-to-camera'
      } else if (props.statusBiometry === 'blockedDevice') {
        return 'blocked-device'
      } else if (props.statusBiometry === 'deviceRegistration') {
        return 'device-reg'
      } else {
        return 'error'
      }
    })

    return {
      ctn_store: ctn,
      ...propRefs,
      updateProp,
      restartBiometry,
      backToStart,
      goToNumberRegistrationPage,
      redirectToMyBeeline,
      redirectToMoreDetails,
      primaryButtonClass,
      statusIcon,
      statusAlt,
      showBackToStartButton,
    }
  },
})
</script>

<template>
  <div v-if="showStatusBiometry" class="status-container">
    <div class="block">
      <div v-if="statusBiometry !== 'maximumNumberExceeded'">
        <img
          :src="statusIcon"
          :alt="statusAlt"
          :class="statusBiometry === 'fatalError' ? 'fatal-error-icon' : 'img-icon'"
        />
      </div>
      <div v-else />
    </div>

    <div class="content">
      <h3>{{ title }}</h3>
      <p v-if="description">
        {{ description }}
      </p>
    </div>

    <div
      v-if="restartDecs"
      class="btn-block"
      :class="{
        'redirect-class': isRedirectToMyBeeline,
      }"
    >
      <ButtonComponent
        v-if="restartIdentification"
        :class="primaryButtonClass"
        class="primary-button"
        @on-click="restartBiometry()"
      >
        {{ restartDecs }}
      </ButtonComponent>

      <div v-if="isRedirectToMyBeeline">
        <ButtonComponent
          v-if="restartIdentification"
          class="primary-button"
          @on-click="redirectToMyBeeline()"
        >
          {{ $t('deviceRegistration.messages.redirectToMyBeeline') }}
        </ButtonComponent>

        <ButtonComponent
          v-else
          class="primary-button"
          @on-click="redirectToMyBeeline()"
        >
          {{ $t('beeLiveScanner.errBtnText.redirectToMyBeeline') }}
        </ButtonComponent>
      </div>

      <ButtonComponent
        v-if="!restartIdentification && noAccessToCamera"
        class="primary-button"
        :class="{
          'redirect-color': statusBiometry === 'noAccessCamera',
        }"
        @on-click="goToNumberRegistrationPage()"
      >
        {{ restartDecs }}
      </ButtonComponent>

      <ButtonComponent
        v-if="showBackToStartButton"
        class="primary-button"
        :class="{ 'redirect-color': statusBiometry === 'deviceRegistration' }"
        @on-click="backToStart()"
      >
        {{ restartDecs }}
      </ButtonComponent>

      <ButtonComponent
        v-if="statusBiometry === 'deviceRegistration'" class="primary-button"
        @on-click="restartBiometry()"
      >
        {{ $t('deviceRegistration.messages.errors.registration.btn1') }}
      </ButtonComponent>

      <div v-if="statusBiometry === 'maximumNumberExceeded'">
        <ButtonComponent
          class="primary-button"
          @on-click="redirectToMoreDetails()"
        >
          {{ $t('phoneRegistration.errors.btnMoreDetails') }}
        </ButtonComponent>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.status-container {
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  border-radius: 12px;
  gap: 24px;
  background: #ffffff;
  box-shadow: 0 4px 16px 0 #10083f1f;

  & .block {
    width: 100%;
    //padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
    height: 100%;

    & .img-icon {
      width: 104px;
      height: 104px;
    }

    & .blocked-device-icon {
      width: 120px;
      height: 120px;
    }

    & .fatal-error-icon {
      width: 232px;
      height: 232px;
    }
  }

  & .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;

    & h3 {
      text-align: center;
    }

    & p {
      font-size: 14px;
      text-align: center;
    }
  }

  & .btn-block {
    gap: 12px;
    display: flex;
    margin: auto;

    .redirect-color {
      background-color: #e2e1e8;
    }
  }

  & .redirect-class {
    display: grid;
    grid-template-columns: auto auto;
    gap: 10px;
  }
}

@media screen and (max-width: 500px) {
  .status-container {
    padding: 63px 16px;
    box-shadow: none;

    & .block {
      & .img-icon {
        width: 104px;
        height: 104px;
      }

      & .fatal-error-icon {
        width: 160px;
        height: 160px;
      }
    }

    & .content {
      h3 {
        font-size: 24px;
      }
    }

    & .redirect-class {
      display: grid;
      grid-template-columns: auto;
      width: 100%;
    }
  }
}

@media screen and (min-width: 560px) {
  .status-container {
    min-width: 520px;
  }
}
</style>
